import { useMemo, type ReactNode } from "react";
import { Tabs, TabPanel, type TabItem } from "@/shared/ui/core/Tabs";
import {
  SegmentedFilter,
  type SegmentedFilterOption,
} from "@/shared/ui/core/SegmentedFilter";
import { cn } from "@/shared/lib/cn";
import { ErrorState } from "@/shared/ui/core/ErrorState";
import { LoadingState } from "@/shared/ui/LoadingState";
import { resolveDetailDefaultTabId } from "@/shared/ui/patterns/detailPageTabPolicy";

export type DetailPageTab = {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
};

export type DetailPageTemplateProps = {
  /** Banner / domain notice above status. */
  notice?: ReactNode;
  /** StatusBadge + meta chips row. */
  status?: ReactNode;
  /**
   * Optional tabs. When set, `children` is ignored.
   * Prefer tab order (R3): Tổng quan → domain* → Liên quan → Tài liệu → Audit.
   * See `DETAIL_TAB_ORDER` / `formatRelatedTabLabel` in detailPageTabPolicy.
   */
  tabs?: DetailPageTab[];
  /** Controlled tab id (optional). */
  tabId?: string;
  defaultTabId?: string;
  onTabChange?: (tabId: string) => void;
  /** Wrap the tab strip in the detail card. Panels stay outside. */
  frameTabs?: boolean;
  /** Mobile-first segmented pill tabs using SegmentedFilter. */
  segmentedTabs?: boolean;
  /** Body when not using tabs. */
  children?: ReactNode;
  loading?: boolean;
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
};

/**
 * Standard Ops WEB detail layout — notice → status → tabs|body.
 * Pair with routed detail pages + `usePageActions` for transitions.
 * Create/edit stays in `EntityFormDialog` (not this template).
 *
 * Anti-patterns: domain mutations inside the template; Drawer as SoT;
 * fake Tài liệu/Audit content without BE (phase T4).
 */
export function DetailPageTemplate({
  notice,
  status,
  tabs,
  tabId,
  defaultTabId,
  onTabChange,
  frameTabs = false,
  segmentedTabs = false,
  children,
  loading,
  errorTitle,
  errorDescription,
  onRetry,
}: DetailPageTemplateProps) {
  const useTabs = Boolean(tabs && tabs.length > 0);
  const resolvedDefault = useTabs
    ? resolveDetailDefaultTabId(tabs!, defaultTabId)
    : undefined;

  const tabItems: TabItem[] = useTabs
    ? tabs!.map((t) => ({
        value: t.id,
        label: t.label,
        disabled: t.disabled,
        icon: t.icon,
      }))
    : [];

  const activeTabId = tabId ?? resolvedDefault ?? tabs?.[0]?.id ?? "";

  const segmentedOptions = useMemo<SegmentedFilterOption[]>(() => {
    if (!tabs) return [];
    return tabs.map((t) => {
      const match = t.label.match(/^(.*?)(?:\s*\((\d+)\))?$/);
      const cleanLabel = match?.[1]?.trim() || t.label;
      const count = match?.[2] != null ? parseInt(match[2], 10) : undefined;
      return {
        value: t.id,
        label: cleanLabel,
        count,
        icon: t.icon,
        disabled: t.disabled,
      };
    });
  }, [tabs]);

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-clip">
      {notice}
      {status}
      {loading ? <LoadingState /> : null}
      {errorTitle ? (
        <ErrorState
          title={errorTitle}
          description={errorDescription}
          onRetry={onRetry}
        />
      ) : null}
      {!loading && !errorTitle && useTabs ? (
        segmentedTabs ? (
          <div className="space-y-3.5">
            <SegmentedFilter
              options={segmentedOptions}
              value={activeTabId}
              onChange={(next) => onTabChange?.(next)}
              scrollable
              size="md"
            />
            {tabs!.map((t) => (
              <div
                key={t.id}
                className={cn(
                  t.id === activeTabId ? "block" : "hidden",
                )}
              >
                {t.content}
              </div>
            ))}
          </div>
        ) : (
          <Tabs
            items={tabItems}
            value={tabId}
            defaultValue={resolvedDefault}
            onValueChange={onTabChange}
            frameList={frameTabs}
          >
            {tabs!.map((t) => (
              <TabPanel key={t.id} value={t.id}>
                {t.content}
              </TabPanel>
            ))}
          </Tabs>
        )
      ) : null}
      {!loading && !errorTitle && !useTabs ? children : null}
    </div>
  );
}
